import { Button, OdinIcon } from '@myosh/odin-components';
import { forwardRef, type Ref, type RefObject, useCallback, useImperativeHandle, useRef } from 'react';
import Dialog, { type DialogRef } from './dialog';

interface AddItemDialogProps {
  onSubmit: () => void;
  errorTextRef: RefObject<HTMLParagraphElement | null>;
  inputRef: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}

const AddItemDialog = ({ onSubmit, errorTextRef, inputRef, disabled }: AddItemDialogProps, ref: Ref<DialogRef>) => {
  const dialogRef = useRef<DialogRef>(null);

  const close = useCallback(() => {
    if (disabled) return;
    dialogRef.current?.close();
  }, []);

  useImperativeHandle(ref, () => ({
    close,
    open: () => dialogRef.current?.open(),
  }));

  return (
    <Dialog
      data-cy="add-item-dialog"
      onCancel={close}
      className="text-gray-1 m-auto w-[256px] rounded-md p-4 outline-hidden"
      ref={dialogRef}
    >
      <div className="flex flex-col gap-4">
        <div className="w-full">
          <div className="mb-2 flex flex-row-reverse">
            <div
              data-cy="add-item-dialog-close"
              title="Close"
              className="text-primary-2 cursor-pointer"
              onClick={close}
            >
              <OdinIcon icon="X" />
            </div>
          </div>
          <label htmlFor="term-input" className="flex self-start text-sm font-bold">
            Add new item
          </label>
          <input
            data-cy="add-item-dialog-input"
            disabled={disabled}
            placeholder="enter new item"
            ref={inputRef}
            name="add-new-item-input"
            className="border-gray-4 flex w-full flex-1 appearance-none border px-2 py-1 text-sm focus:outline-hidden"
          />
          <p data-cy="add-item-dialog-error" className="text-error hidden text-xs" ref={errorTextRef} />
        </div>
        <Button data-cy="add-item-dialog-submit" disabled={disabled} onClick={onSubmit}>
          Submit
        </Button>
      </div>
    </Dialog>
  );
};

export default forwardRef(AddItemDialog);
